// Client/src/App.tsx
import { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import Login from './pages/Login';
import { KeyboardProvider } from './Keyboard/KeyboardContext';
import { useAuthStore } from './store/authStore';
import { TaskDetailsModalProvider } from './contexts/TaskDetailsModalContext';
import { ScannerProvider } from './scanner/ScannerProvider';
import { lazyWithReload } from './utils/lazyWithReload';
import adminSystemService from './services/AdminSystemService';
import appSocketService from './services/AppSocketService';
import MaintenanceBanner from './components/MaintenanceBanner';
import nativeClientService from './services/NativeClientService';
import type { SocketAuthErrorEvent } from './services/SocketClient';
import { initializeTaskSocketListeners } from './store/taskStore';
import UiRouteTracker from './Error/UiRouteTracker';
import {
  hasNativeDevSimulatorSession,
  isNativeClientUserAgent,
  isNativeDevSimulatorEnabled,
  isNativeShellMode,
} from './utils/nativeClientEnv';
import { isBackgroundSensitiveMobileClient } from './utils/pushEnvironment';

const Dashboard = lazyWithReload(() => import('./pages/Dashboard'));
const Tasks = lazyWithReload(() => import('./pages/Tasks'));
const MarketPlanPage = lazyWithReload(() => import('./pages/MarketPlanPage'));
const Stores = lazyWithReload(() => import('./pages/Stores'));
const Users = lazyWithReload(() => import('./pages/Users'));
const Settings = lazyWithReload(() => import('./pages/Settings'));
const Katalog = lazyWithReload(() => import('./pages/KatalogPage'));
const Export = lazyWithReload(() => import('./pages/Export'));
const Archiv = lazyWithReload(() => import('./pages/Archiv'));
const HMD = lazyWithReload(() => import('./pages/HausmeisterDienstPage'));
const ReinigungsPage = lazyWithReload(() => import('./pages/ReinigungsPage'));
const ReinigungsUebersichtPage = lazyWithReload(() => import('./pages/ReinigungsUebersichtPage'));
const RolliPage = lazyWithReload(() => import('./pages/RolliPage'));
const WeCheckPage = lazyWithReload(() => import('./pages/WeCheckPage'));
const LayoutBausteinPage = lazyWithReload(() => import('./pages/LayoutBausteinPage'));
const Planner = lazyWithReload(() => import('./pages/Planner'));
const KampangenPage = lazyWithReload(() => import('./pages/KampangenPage'));
const TickerPage = lazyWithReload(() => import('./pages/TickerPage'));
const NativeLogin = lazyWithReload(() => import('./pages/NativeLogin'));
const GuestLogin = lazyWithReload(() => import('./pages/GuestLogin'));
const GuestProfilePage = lazyWithReload(() => import('./pages/GuestProfilePage'));
const ProfilePage = lazyWithReload(() => import('./pages/ProfilePage'));
const GuestDevicesPage = lazyWithReload(() => import('./pages/GuestDevicesPage'));
const NachweisePage = lazyWithReload(() => import('./pages/NachweisePage'));
const ImagePoolPage = lazyWithReload(() => import('./pages/ImagePoolPage'));
const ReinigungBeanstandungReportPage = lazyWithReload(() => import('./pages/ReinigungBeanstandungReportPage'));
const ZeiterfassungPage = lazyWithReload(() => import('./pages/ZeiterfassungPage'));
const ZeiterfassungAnalysePage = lazyWithReload(() => import('./pages/ZeiterfassungAnalysePage'));
const ClientStatus = lazyWithReload(() => import('./pages/ClientStatus'));
const TicketsPage = lazyWithReload(() => import('./pages/TicketsPage'));
const AdminSystem = lazyWithReload(() => import('./pages/AdminSystem'));
const ScannerTestPage = lazyWithReload(() => import('./pages/ScannerTestPage'));
const PublicFormularPage = lazyWithReload(() => import('./pages/public/PublicFormularPage'));
const PublicMhdPage = lazyWithReload(() => import('./pages/PublicMhdPage'));
const FormularTemplatesPage = lazyWithReload(() => import('./pages/admin/FormularTemplatesPage'));
const FormularTemplateUploadPage = lazyWithReload(() => import('./pages/admin/FormularTemplateUploadPage'));
const FormularTemplateEditorPage = lazyWithReload(() => import('./pages/admin/FormularTemplateEditorPage'));
const RolliTimeEstimatesAdminPage = lazyWithReload(() => import('./pages/admin/RolliTimeEstimatesAdminPage'));
const GoodsReceiptPage = lazyWithReload(() => import('./pages/admin/GoodsReceiptPage'));
const ZeitKategorienVerwaltungPage = lazyWithReload(() => import('./pages/admin/ZeitKategorienVerwaltungPage'));
const DMArtikelAdminPage = lazyWithReload(() => import('./pages/admin/DMArtikelAdminPage'));
const MhdAuswertungPage = lazyWithReload(() => import('./pages/admin/MhdAuswertungPage'));
const MhdSettingsPage = lazyWithReload(() => import('./pages/admin/MhdSettingsPage'));
const DeliveryPlansPage = lazyWithReload(() => import('./pages/DeliveryPlansPage'));
const RevisionDefinitionsPage = lazyWithReload(() => import('./pages/RevisionDefinitionsPage'));
const RevisionAnalyticsPage = lazyWithReload(() => import('./pages/RevisionAnalyticsPage'));
const StoreFormularUebersichtPage = lazyWithReload(() => import('./pages/StoreFormularUebersichtPage'));
const DMKategorienPage = lazyWithReload(() => import('./pages/DMKategorienPage'));
const SperrtageVerwaltungPage = lazyWithReload(() => import('./pages/SperrtageVerwaltungPage'));
const TagesKapazitaetenPage = lazyWithReload(() => import('./pages/TagesKapazitaetenPage'));
const ShelfTemplatesPage = lazyWithReload(() => import('./pages/ShelfTemplatesPage'));
const ShelfLayoutsPage = lazyWithReload(() => import('./pages/ShelfLayoutsPage'));
const ShelfLayoutEditorPage = lazyWithReload(() => import('./pages/ShelfLayoutEditorPage'));
const ShelfActionsPage = lazyWithReload(() => import('./pages/ShelfActionsPage'));
const ShelfActionLinkReportsPage = lazyWithReload(() => import('./pages/ShelfActionLinkReportsPage'));
const SecondaryPlacementReportsPage = lazyWithReload(() => import('./pages/SecondaryPlacementReportsPage'));
const VirtualKeyboard = lazyWithReload(() => import('./Keyboard/VirtualKeyboard'));

function FullscreenRouteFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6 text-sm font-medium text-slate-500">
      Inhalt wird geladen...
    </div>
  );
}

function renderFullscreenRoute(element: JSX.Element) {
  return (
    <Suspense fallback={<FullscreenRouteFallback />}>
      {element}
    </Suspense>
  );
}

export default function App() {
  /* ---------- Sidebar-State (lokal) ---------- */
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar  = useCallback(() => setSidebarOpen((prev) => !prev), []);
  const closeSidebar   = useCallback(() => setSidebarOpen(false), []);

  /* ---------- Auth + Socket ---------- */
  const status = useAuthStore((s) => s.status);
  const user = useAuthStore((s) => s.user);
  const selectedStoreId = useAuthStore((s) => s.selectedStoreId);
  const isCleaningStaff = useAuthStore((s) => s.isCleaningStaff);
  const isCleaningPortalUser = useAuthStore((s) => s.isCleaningPortalUser);
  const fetchTokenAndUser = useAuthStore((s) => s.fetchTokenAndUser);
  const socketConnectAttemptedRef = useRef(false);
  const isRefreshingTokenRef = useRef(false);
  const userRole = user?.role;
  const canViewShelfActionLinkReports = userRole === 'admin' || (user?.permissions || []).includes('shelf_action_link_reports');
  const isKatalogBlockedRole =
    userRole === 'reinigungskraft' || userRole === 'manager_reinigungskraft';
  const katalogRedirectTarget =
    userRole === 'manager_reinigungskraft' ? '/reinigung/uebersicht' : '/reinigung';
  const isAnonymousGuest = userRole === 'anonymous_guest' || user?.isAnonymousGuest === true;
  const isTabletSession = userRole === 'tablet';
  const isNativeShell = isNativeShellMode();
  const isRealNativeShell = isNativeClientUserAgent();
  const isNativeDevSimulator = isNativeDevSimulatorEnabled();
  const isTabletEnvironment = isNativeShell || isTabletSession;
  const pauseHeartbeatWhenHidden = isBackgroundSensitiveMobileClient();
  const hasAuthenticatedUser = Boolean(user);
  const cleaningStaffUser = isCleaningStaff();
  const cleaningPortalUser = isCleaningPortalUser();
  const layoutDesignerUser = userRole === 'layoutdesigner';
  const adminOrMarketLead =
    userRole ? ['admin', 'filialleiter', 'vertretenderfilialleiter'].includes(userRole) : false;
  const canAccessRevision =
    userRole ? ['admin', 'filialleiter', 'vertretenderfilialleiter'].includes(userRole) : false;
  const employeeId = user?.employee_id;

  const blockGuestRoute = useCallback(
    (element: JSX.Element, fallback = '/') => (isAnonymousGuest ? <Navigate to={fallback} replace /> : element),
    [isAnonymousGuest]
  );

  const blockCleaningPortalAndGuestRoute = useCallback(
    (element: JSX.Element) => {
      if (isAnonymousGuest) {
        return <Navigate to="/" replace />;
      }
      if (layoutDesignerUser) {
        return <Navigate to="/shelf/layouts" replace />;
      }
      if (cleaningPortalUser) {
        return <Navigate to="/reinigung" replace />;
      }
      return element;
    },
    [cleaningPortalUser, isAnonymousGuest, layoutDesignerUser]
  );

  const blockCleaningPortalRoute = useCallback(
    (element: JSX.Element) => (cleaningPortalUser ? <Navigate to="/reinigung" replace /> : element),
    [cleaningPortalUser]
  );

  const blockCleaningAndGuestRoute = useCallback(
    (element: JSX.Element) => (
      isAnonymousGuest || cleaningStaffUser
        ? <Navigate to="/" replace />
        : element
    ),
    [cleaningStaffUser, isAnonymousGuest]
  );

  const blockLayoutDesignerRoute = useCallback(
    (element: JSX.Element) => (layoutDesignerUser ? <Navigate to="/shelf/layouts" replace /> : element),
    [layoutDesignerUser]
  );

  useEffect(() => {
    if (status === 'idle') {
      if (isNativeDevSimulator && !hasNativeDevSimulatorSession()) {
        return;
      }
      void fetchTokenAndUser().catch((error) => {
        console.warn('App: Initiales Bootstrap fehlgeschlagen:', error);
      });
    }
  }, [status, fetchTokenAndUser, isNativeDevSimulator]);

  const connectAllSockets = useCallback(async (employeeId?: string) => {
    if (!employeeId) {
      console.warn('App: Keine Employee-ID für Socket-Verbindung verfügbar');
      return;
    }
    console.log('⏱️ App: Starte Socket-Verbindungen für', employeeId);
    try {
      console.log(`App: Verbinde AppSocketService (main) für Employee ${employeeId}`);

      const mainConnected = await appSocketService.connect(employeeId);
      if (mainConnected) {
        console.log('App: AppSocketService (main) erfolgreich verbunden');
      } else {
        console.error('App: AppSocketService (main) Verbindung fehlgeschlagen');
      }

      if (userRole === 'admin') {
        console.log('App: Initialisiere AdminSystemService (admin)');
        const adminConnected = await adminSystemService.connect(employeeId);
        if (adminConnected) {
          console.log('App: AdminSystemService (admin) erfolgreich verbunden');
        } else {
          console.error('App: AdminSystemService (admin) Verbindung fehlgeschlagen');
        }

        console.log('App: Initialisiere NativeClientService (admin)');
        const nativeConnected = await nativeClientService.connect(employeeId, undefined, { connectionType: 'admin' });
        if (nativeConnected) {
          console.log('App: NativeClientService (admin) erfolgreich verbunden');
        } else {
          console.error('App: NativeClientService (admin) Verbindung fehlgeschlagen');
        }
      }

      console.log('App: Alle Socket.IO-Verbindungen initialisiert');
    } catch (error) {
      console.error('App: Fehler beim Herstellen der Socket.IO-Verbindung:', error);
    }
  }, [userRole]);

  useEffect(() => {
    const isAuthenticated = status === 'authenticated' && Boolean(employeeId);
    if (isAuthenticated && !socketConnectAttemptedRef.current) {
      console.log('App: Benutzer angemeldet, starte Socket.IO-Verbindung...');
      console.log('🔍 Session-Status:', status);
      console.log('🔍 User vorhanden:', Boolean(employeeId));
      console.log('🔍 User Employee ID:', employeeId);
      console.log('🔍 socketConnectAttempted:', socketConnectAttemptedRef.current);

      // Verhindere mehrfache Socket-Verbindungen durch sofortige Markierung
      socketConnectAttemptedRef.current = true;

      console.log('🔧 App: Registriere setTimeout für Socket-Verbindung (500ms)...');
      
      // Kurze Verzögerung für stabilen Verbindungsaufbau
      const timeoutId = setTimeout(() => {
        console.log('⏱️ App: Timeout erreicht, starte Socket-Verbindung...');
        void connectAllSockets(employeeId);
      }, 500);

      // Cleanup nur wenn Component unmounted - NICHT bei Re-Render!
      return () => {
        // Nur cleanup wenn wirklich unmounted bzw. Session endet
        if (!isAuthenticated) {
          console.log('🧹 App: Cleanup - clearTimeout aufgerufen (Component unmount)');
          clearTimeout(timeoutId);
        }
      };
    }
  }, [status, employeeId, connectAllSockets]);

  useEffect(() => {
    const handleSocketAuthError = async (event: SocketAuthErrorEvent) => {
      console.warn('⚠️ Socket: Authentifizierungsproblem erkannt:', event);
      if (isRefreshingTokenRef.current) {
        console.log('🔁 Socket: Token-Erneuerung läuft bereits, überspringe weiteren Versuch');
        return;
      }

      isRefreshingTokenRef.current = true;

      try {
        await fetchTokenAndUser();

        const state = useAuthStore.getState();
        const refreshedUserId = state.user?.employee_id;

        if (!refreshedUserId) {
          console.error('Socket: Kein gültiger Benutzer nach Token-Erneuerung vorhanden – logout');
          await state.logout();
          return;
        }

        console.log('🔄 Socket: Token erfolgreich erneuert – stelle Verbindungen neu her');
        socketConnectAttemptedRef.current = true;
        await connectAllSockets(refreshedUserId);
      } catch {
        console.error('❌ Socket: Token-Erneuerung nach Auth-Fehler fehlgeschlagen');
        await useAuthStore.getState().logout();
      } finally {
        isRefreshingTokenRef.current = false;
      }
    };

    appSocketService.onAuthError(handleSocketAuthError);
    adminSystemService.onAuthError(handleSocketAuthError);
    nativeClientService.onAuthError(handleSocketAuthError);

    return () => {
      appSocketService.offAuthError(handleSocketAuthError);
      adminSystemService.offAuthError(handleSocketAuthError);
      nativeClientService.offAuthError(handleSocketAuthError);
    };
  }, [fetchTokenAndUser, connectAllSockets]);

  useEffect(() => {
    if (status !== 'authenticated') {
      socketConnectAttemptedRef.current = false;
      // Socket trennen wenn kein Token - alle Services
      console.log('App: Token entfernt, trenne Socket-Verbindungen');
      appSocketService.disconnect();
      // Trenne Admin- und Native-Services unabhängig vom Client-Typ,
      // da sie beim erneuten Login ohnehin neu verbunden werden müssen
      adminSystemService.disconnect();
      nativeClientService.disconnect();
    }
  }, [status]);

  // AdminSystemService und NativeClientService werden über die Socket-Initialisierung gesteuert.
  // HINWEIS: Cleanup wird nur beim Sitzungsende durchgeführt (siehe useEffect mit status dependency)

  /* ---------- Token-Heartbeat: Prüft regelmäßig, ob Token noch gültig ist ---------- */
  useEffect(() => {
    if (status !== 'authenticated' || !hasAuthenticatedUser) return;
    if (isRealNativeShell) {
      // Native-Core übernimmt Heartbeat/Session-Handling
      return;
    }

    const heartbeatInterval = isTabletEnvironment ? 30 * 60 * 1000 : 2 * 60 * 60 * 1000;
    const runHeartbeat = async () => {
      if (pauseHeartbeatWhenHidden && document.visibilityState === 'hidden') {
        console.log('Heartbeat: mobile Hintergrundansicht erkannt, ueberspringe Polling.');
        return;
      }

      console.log('Heartbeat: Überprüfe Session-Gültigkeit...');
      try {
        await useAuthStore.getState().fetchUser();
        console.log('Heartbeat: Session weiterhin gültig');
      } catch {
        console.warn('Heartbeat: Session konnte nicht aktualisiert werden, versuche Re-Bootstrap');
        try {
          await useAuthStore.getState().fetchTokenAndUser();
        } catch (refreshError) {
          console.error('Heartbeat: Re-Bootstrap fehlgeschlagen:', refreshError);
        }
      }
    };

    const heartbeatTimer = setInterval(runHeartbeat, heartbeatInterval);
    const initialCheckTimer = setTimeout(runHeartbeat, 60 * 1000);
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void runHeartbeat();
      }
    };

    if (pauseHeartbeatWhenHidden) {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    return () => {
      clearInterval(heartbeatTimer);
      clearTimeout(initialCheckTimer);
      if (pauseHeartbeatWhenHidden) {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
    };
  }, [status, userRole, isTabletEnvironment, isRealNativeShell, pauseHeartbeatWhenHidden, hasAuthenticatedUser]);

  useEffect(() => {
    if (status !== 'authenticated' || !employeeId || !selectedStoreId) {
      return;
    }
    const cleanup = initializeTaskSocketListeners();
    return () => {
      cleanup();
    };
  }, [status, employeeId, selectedStoreId]);

  return (
    <TaskDetailsModalProvider>
      <KeyboardProvider isKioskClient={isTabletEnvironment}>
        <ScannerProvider>
        <MaintenanceBanner />
        <BrowserRouter>
          <UiRouteTracker />
          <Routes>
            {/* Öffentliche Routen OHNE Auth, immer verfügbar */}
            <Route path="/formular/:token" element={renderFullscreenRoute(<PublicFormularPage />)} />
            <Route path="/public/form/:token" element={renderFullscreenRoute(<PublicFormularPage />)} />
            <Route path="/mhd-erfassung/:token" element={renderFullscreenRoute(<PublicMhdPage />)} />
            <Route path="/guest-login" element={renderFullscreenRoute(<GuestLogin />)} />

            {!user ? (
              <>
                <Route path="/native-login" element={renderFullscreenRoute(<NativeLogin />)} />
                <Route path="*" element={isNativeShell ? renderFullscreenRoute(<NativeLogin />) : <Login />} />
              </>
            ) : (
              <Route
                path="/"
                element={
                  <Layout
                    sidebarOpen={sidebarOpen}
                    toggleSidebar={toggleSidebar}
                    closeSidebar={closeSidebar}
                  />
                }
              >
                {/* Reinigungskräfte werden automatisch zur Übersichtsseite weitergeleitet */}
                <Route
                  index
                  element={
                    layoutDesignerUser ? (
                      <Navigate to="/shelf/layouts" replace />
                    ) :
                    cleaningPortalUser && !isAnonymousGuest ? (
                      <Navigate to="/reinigung/uebersicht" replace />
                    ) : (
                      <Dashboard />
                    )
                  }
                />
                
                {/* Normale Benutzer-Routen */}
                <Route path="tasks" element={blockCleaningPortalAndGuestRoute(<Tasks />)} />
                <Route path="marktplan" element={blockCleaningPortalAndGuestRoute(<MarketPlanPage />)} />
                <Route path="tickets" element={blockCleaningPortalRoute(blockLayoutDesignerRoute(<TicketsPage />))} />
                <Route path="stores" element={blockCleaningPortalAndGuestRoute(<Stores />)} />
                <Route
                  path="users"
                  element={blockLayoutDesignerRoute(blockGuestRoute(cleaningStaffUser ? <Navigate to="/reinigung" replace /> : <Users />))}
                />
                <Route path="hausmeister" element={blockCleaningPortalRoute(blockLayoutDesignerRoute(<HMD />))} />
                <Route path="settings" element={blockCleaningPortalAndGuestRoute(<Settings />)} />
                <Route
                  path="settings/secondary-placement-reports"
                  element={isAnonymousGuest ? <Navigate to="/" replace /> : userRole === 'admin' ? <SecondaryPlacementReportsPage /> : <Navigate to="/" replace />}
                />
                <Route
                  path="completed-today"
                  element={layoutDesignerUser ? <Navigate to="/shelf/layouts" replace /> : isAnonymousGuest ? <Navigate to="/" replace /> : <Navigate to="/verlauf" replace />}
                />
                <Route
                  path="katalog"
                  element={
                    layoutDesignerUser ? (
                      <Navigate to="/shelf/layouts" replace />
                    ) : isAnonymousGuest ? (
                      <Navigate to="/" replace />
                    ) : isKatalogBlockedRole ? (
                      <Navigate to={katalogRedirectTarget} replace />
                    ) : (
                      <Katalog />
                    )
                  }
                />
                <Route path="verlauf" element={blockCleaningPortalAndGuestRoute(<Archiv />)} />
                <Route
                  path="archiv"
                  element={layoutDesignerUser ? <Navigate to="/shelf/layouts" replace /> : isAnonymousGuest ? <Navigate to="/" replace /> : <Navigate to="/verlauf" replace />}
                />
                <Route path="export" element={blockCleaningPortalAndGuestRoute(<Export />)} />
                <Route path="rolli" element={cleaningPortalUser && !isAnonymousGuest ? <Navigate to="/reinigung" replace /> : <RolliPage />} />
                <Route path="we-check" element={cleaningPortalUser && !isAnonymousGuest ? <Navigate to="/reinigung" replace /> : <WeCheckPage />} />
                <Route
                  path="layoutbaustein"
                  element={isAnonymousGuest ? <Navigate to="/" replace /> : userRole === 'admin' ? <LayoutBausteinPage /> : <Navigate to="/" replace />}
                />
                <Route
                  path="dm-kategorien"
                  element={isAnonymousGuest ? <Navigate to="/" replace /> : layoutDesignerUser ? <Navigate to="/shelf/layouts" replace /> : userRole === 'admin' ? <DMKategorienPage /> : <Navigate to="/" replace />}
                />
                <Route path="sperrtage" element={blockCleaningPortalAndGuestRoute(<SperrtageVerwaltungPage />)} />
                <Route path="tages-kapazitaeten" element={blockCleaningPortalAndGuestRoute(<TagesKapazitaetenPage />)} />
                <Route path="anlieferplan" element={isAnonymousGuest ? <Navigate to="/" replace /> : userRole === 'admin' ? <DeliveryPlansPage /> : <Navigate to="/" replace />} />
                <Route path="admin/wareneingang" element={isAnonymousGuest ? <Navigate to="/" replace /> : userRole === 'admin' ? <GoodsReceiptPage /> : <Navigate to="/" replace />} />
                <Route path="shelf/actions" element={layoutDesignerUser ? <Navigate to="/shelf/layouts" replace /> : blockCleaningPortalRoute(<ShelfActionsPage />)} />
                <Route
                  path="shelf/action-link-reports"
                  element={layoutDesignerUser || !canViewShelfActionLinkReports ? <Navigate to="/" replace /> : blockCleaningPortalRoute(<ShelfActionLinkReportsPage />)}
                />
                <Route path="shelf/layouts" element={blockCleaningPortalRoute(<ShelfLayoutsPage />)} />
                <Route path="shelf/layouts/:layoutId/edit" element={blockCleaningPortalRoute(<ShelfLayoutEditorPage />)} />
                <Route
                  path="admin/shelfs"
                  element={isAnonymousGuest ? <Navigate to="/" replace /> : ['admin', 'layoutdesigner'].includes(userRole || '') ? <ShelfTemplatesPage /> : <Navigate to="/" replace />}
                />
                <Route
                  path="admin/dm-artikel"
                  element={isAnonymousGuest ? <Navigate to="/" replace /> : userRole === 'admin' ? <DMArtikelAdminPage /> : <Navigate to="/" replace />}
                />
                <Route
                  path="admin/mhd"
                  element={blockCleaningAndGuestRoute(<MhdAuswertungPage />)}
                />
                <Route
                  path="admin/mhd-einstellungen"
                  element={isAnonymousGuest ? <Navigate to="/" replace /> : userRole === 'admin' ? <MhdSettingsPage /> : <Navigate to="/" replace />}
                />
                <Route path="shelf/templates" element={<Navigate to="/admin/shelfs" replace />} />
                <Route path="planner" element={blockCleaningPortalAndGuestRoute(<Planner />)} />
                <Route path="kampagnen" element={blockCleaningPortalAndGuestRoute(<KampangenPage />)} />
                <Route path="mitteilungen" element={blockCleaningPortalAndGuestRoute(<TickerPage />)} />
                <Route path="nachweise" element={blockCleaningPortalAndGuestRoute(<NachweisePage />)} />
                <Route path="bildpool" element={blockCleaningPortalAndGuestRoute(<ImagePoolPage />)} />
                <Route path="zeiterfassung" element={blockCleaningPortalRoute(blockLayoutDesignerRoute(<ZeiterfassungPage />))} />
                <Route path="zeiterfassung-analyse" element={blockCleaningPortalAndGuestRoute(<ZeiterfassungAnalysePage />)} />
                <Route
                  path="revisions/auswertung"
                  element={
                    layoutDesignerUser ? (
                      <Navigate to="/shelf/layouts" replace />
                    ) : isAnonymousGuest ? (
                      <Navigate to="/" replace />
                    ) : canAccessRevision ? (
                      <RevisionAnalyticsPage />
                    ) : (
                      <Navigate to="/" replace />
                    )
                  }
                />
                
                {/* Reinigungsplan für alle verfügbar */}
                <Route path="reinigung" element={layoutDesignerUser ? <Navigate to="/shelf/layouts" replace /> : <ReinigungsPage />} />
                <Route
                  path="reinigung/uebersicht"
                  element={layoutDesignerUser ? <Navigate to="/shelf/layouts" replace /> : isAnonymousGuest ? <Navigate to="/reinigung" replace /> : <ReinigungsUebersichtPage />}
                />
                <Route
                  path="reinigung/auswertung"
                  element={layoutDesignerUser ? <Navigate to="/shelf/layouts" replace /> : isAnonymousGuest ? <Navigate to="/reinigung" replace /> : <ReinigungBeanstandungReportPage />}
                />
                <Route
                  path="profil"
                  element={isTabletEnvironment ? <Navigate to="/" replace /> : isAnonymousGuest ? <GuestProfilePage /> : <ProfilePage />}
                />
                <Route
                  path="gastgeraete"
                  element={
                    layoutDesignerUser ? (
                      <Navigate to="/shelf/layouts" replace />
                    ) : isAnonymousGuest ? (
                      <Navigate to="/" replace />
                    ) : adminOrMarketLead ? (
                      <GuestDevicesPage />
                    ) : (
                      <Navigate to="/" replace />
                    )
                  }
                />

                {/* Admin-Seiten für Administratoren */}
                <Route path="client-status" element={blockLayoutDesignerRoute(blockGuestRoute(<ClientStatus />))} />
                <Route
                  path="revisions/admin"
                  element={
                    layoutDesignerUser ? (
                      <Navigate to="/shelf/layouts" replace />
                    ) : isAnonymousGuest ? (
                      <Navigate to="/" replace />
                    ) : userRole === 'admin' ? (
                      <RevisionDefinitionsPage />
                    ) : (
                      <Navigate to="/" replace />
                    )
                  }
                />
                <Route path="admin-system" element={blockLayoutDesignerRoute(blockGuestRoute(<AdminSystem />))} />
                <Route
                  path="scanner-test"
                  element={isAnonymousGuest ? <Navigate to="/" replace /> : userRole === 'admin' ? <ScannerTestPage /> : <Navigate to="/" replace />}
                />

                {/* Formular-Templates unter Einstellungen */}
                <Route
                  path="einstellungen/formular-templates"
                  element={isAnonymousGuest ? <Navigate to="/" replace /> : userRole === 'admin' ? <FormularTemplatesPage /> : <Navigate to="/" replace />}
                />
                <Route
                  path="einstellungen/formular-templates/neu"
                  element={isAnonymousGuest ? <Navigate to="/" replace /> : userRole === 'admin' ? <FormularTemplateUploadPage /> : <Navigate to="/" replace />}
                />
                <Route
                  path="einstellungen/formular-templates/:id/editor"
                  element={isAnonymousGuest ? <Navigate to="/" replace /> : userRole === 'admin' ? <FormularTemplateEditorPage /> : <Navigate to="/" replace />}
                />
                <Route
                  path="einstellungen/rolli-zeitvorgaben"
                  element={layoutDesignerUser ? <Navigate to="/shelf/layouts" replace /> : isAnonymousGuest ? <Navigate to="/" replace /> : userRole === 'admin' ? <RolliTimeEstimatesAdminPage /> : <Navigate to="/" replace />}
                />
                <Route
                  path="einstellungen/zeitkategorien"
                  element={layoutDesignerUser ? <Navigate to="/shelf/layouts" replace /> : isAnonymousGuest ? <Navigate to="/" replace /> : userRole === 'admin' ? <ZeitKategorienVerwaltungPage /> : <Navigate to="/" replace />}
                />

                {/* Formular-Übersicht für alle Benutzer */}
                <Route path="formular-uebersicht" element={blockCleaningPortalRoute(blockLayoutDesignerRoute(<StoreFormularUebersichtPage />))} />
                <Route path="*" element={<Navigate to={layoutDesignerUser ? '/shelf/layouts' : '/'} replace />} />
              </Route>
            )}
          </Routes>
        </BrowserRouter>
        <Suspense fallback={null}>
          <VirtualKeyboard />
        </Suspense>
        </ScannerProvider>
      </KeyboardProvider>
    </TaskDetailsModalProvider>
  );
}
